import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useId } from "react";

import { DailySalesView } from "~/components/reports/employee-performance/views/daily-sales-view";
import { TipsView } from "~/components/reports/employee-performance/views/tips-view";
import { WeeklyEarningsView } from "~/components/reports/employee-performance/views/weekly-earnings-view";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import {
  useSearchParamsDateRange,
  requireDateRange,
} from "~/utils/search-params-date-range";

const EMP_PERF_GROUP_BY = ["daily-sales", "weekly-earnings", "tips"] as const;
type GroupByType = (typeof EMP_PERF_GROUP_BY)[number];
const isEmpPerfGroupBy = enumIncludes(EMP_PERF_GROUP_BY);

export const Route = createFileRoute(
  "/_authenticated/reports/employee-performance"
)({
  component: EmployeePerformanceReport,
  validateSearch: (search: Record<string, unknown>) => ({
    location: typeof search.location === "string" ? search.location : undefined,
    employee: typeof search.employee === "string" ? search.employee : undefined,
    groupBy: typeof search.groupBy === "string" ? search.groupBy : undefined,
  }),
});

function EmployeePerformanceReport() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const employeeId = searchParams.employee;
  const groupBy = parseEnumValue(
    searchParams.groupBy,
    isEmpPerfGroupBy,
    "daily-sales"
  );

  const [dateRange] = useSearchParamsDateRange();

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Employee
            Performance.
          </p>
        </div>
      </div>
    );
  }

  if (!(dateRange.from && dateRange.to)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Date Range Required</h3>
          <p className="body text-muted-foreground">
            Please select a date range from the filters above.
          </p>
        </div>
      </div>
    );
  }

  const typedDateRange = requireDateRange(dateRange);
  if (!typedDateRange) {
    return null;
  }

  return (
    <EmployeePerformanceContent
      dateRange={typedDateRange}
      employeeId={employeeId ?? null}
      groupBy={groupBy}
      locationId={locationId}
    />
  );
}

const EmployeePerformanceContent = ({
  locationId,
  employeeId,
  groupBy,
  dateRange,
}: {
  locationId: string;
  employeeId: string | null;
  groupBy: GroupByType;
  dateRange: { from: Date; to: Date };
}) => {
  const tableId = useId();

  const getTitle = () => {
    switch (groupBy) {
      case "daily-sales":
        return "Daily Sales Performance";
      case "weekly-earnings":
        return "Weekly Earnings Summary";
      case "tips":
        return "Tips Analysis";
      default:
        return "Employee Performance";
    }
  };

  const getDescription = () => {
    switch (groupBy) {
      case "daily-sales":
        return "Employee sales performance aggregated by overall performance metrics";
      case "weekly-earnings":
        return "Weekly earnings breakdown with hours worked and tips earned";
      case "tips":
        return "Detailed tips analysis by employee and date";
      default:
        return "Employee performance data";
    }
  };

  const renderView = () => {
    switch (groupBy) {
      case "daily-sales":
        return (
          <DailySalesView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "weekly-earnings":
        return (
          <WeeklyEarningsView
            dateRange={dateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "tips":
        if (!employeeId) {
          return (
            <div className="flex h-64 items-center justify-center">
              <div className="text-center">
                <h3 className="subtitle text-foreground mb-2">
                  Employee Required
                </h3>
                <p className="body text-muted-foreground">
                  Please select an employee from the filters above to view tips
                  data.
                </p>
              </div>
            </div>
          );
        }
        return (
          <TipsView
            dateRange={dateRange}
            employeeId={employeeId}
            locationId={locationId}
            tableId={tableId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">{getTitle()}</h2>
        <p className="body text-muted-foreground">{getDescription()}</p>
      </div>

      {renderView()}
    </div>
  );
};
